// import styled from 'styled-components';
import React, { ReactNode, useState } from 'react';
import Select, {
  DropdownIndicatorProps,
  GroupBase,
  StylesConfig,
  components,
} from 'react-select';

import MaterialSymbol from 'components/basic/icons/MaterialSymbol';
import { useIsMobile } from 'hooks/useIsMobile';

// https://react-select.com/styles
const selectStyles = (
  /** No-border transparent style */
  noBorder?: boolean,
  alignTextRight?: boolean,
  slim?: boolean,
  singleValueColor?: string,
  noPaddingRight?: boolean
): StylesConfig => ({
  option: (provided, state) => {
    const background = state.isSelected
      ? 'var(--primary3)'
      : state.isFocused
      ? 'var(--separator)'
      : 'initial';
    return {
      ...provided,
      border: 'none',
      background,
      cursor: 'pointer',
      paddingBottom: '4px',
      paddingTop: '4px',
      lineHeight: 'normal',
      // To help align icons (Market dropdown)
      minHeight: '30px',
      display: 'flex',
      alignItems: 'center',
      '&:active': {
        filter: 'brightness(1.2)',
        background,
      },
    };
  },
  menu: provided => ({
    ...provided,
    right: 0,
    background: 'var(--mainDark)',
    marginTop: 0,
    border: '1px solid var(--separator)',
    borderTop: 'none',
    zIndex: 9999, // Make sure it's over Buttons (eg. in BuySellPanel)
    borderRadius: 0,
  }),
  menuList: provided => ({
    ...provided,
    paddingTop: 0,
  }),
  // dropdownIndicator: provided => ({
  //   ...provided,
  //   '> svg': { color: noBorder ? 'white' : '' },
  // }),
  //   state.isDisabled ? { ...provided, display: 'none' } : provided,
  control: (provided, state) => {
    return {
      ...provided,
      boxShadow: 'none',
      flexWrap: 'nowrap',
      height: slim ? 24 : 40,
      minHeight: slim ? 24 : 40,
      background: noBorder ? 'initial' : 'var(--mainDark)',
      borderRadius: state.menuIsOpen ? '5px 5px 0 0' : 5,
      whiteSpace: 'nowrap',
      opacity: state.isDisabled ? 0.7 : 'initial',
      ...(noBorder
        ? {
            borderWidth: 0,
            paddingRight: noPaddingRight ? 0 : '4px',
          }
        : {
            borderWidth: 1,
            borderColor: state.menuIsOpen ? 'var(--mainLight)' : 'transparent',
          }),
      cursor: state.isDisabled ? 'initial' : 'pointer',
      '&:hover, &:focus, &:active': noBorder
        ? {
            borderColor: 'var(--gray3)',
          }
        : { borderColor: 'var(--gray0b)' },

      // Arrow icon style
      ' [class $= "-indicatorContainer"]': {
        padding: slim ? 1 : '8px 0',
        opacity: state.isDisabled ? '0.4' : 'initial',
      },
    };
  },
  placeholder: provided => ({
    ...provided,
    whiteSpace: 'nowrap',
    lineHeight: 1,
  }),
  valueContainer: provided => ({
    ...provided,
    padding: '2px 0 2px 8px',
    ...(alignTextRight ? { justifyItems: 'flex-end' } : {}),
  }),
  singleValue: provided => ({
    ...provided,
    overflow: 'initial',
    color: singleValueColor || 'var(--white)',
    ...(alignTextRight
      ? {
          width: 'calc(100% - 8px)',
          textAlign: 'right',
          paddingRight: 0,
        }
      : {}),
  }),
  input: provided => ({
    ...provided,
    color: 'var(--white)',
  }),
  indicatorSeparator: () => ({ display: 'none' }),
  indicatorsContainer: provided => ({
    ...provided,
    paddingRight: noPaddingRight ? 0 : 7,
  }),
});

const DropdownIndicator = (
  props: DropdownIndicatorProps<unknown, boolean, GroupBase<unknown>>
) => {
  return (
    <components.DropdownIndicator {...props}>
      {props.selectProps.menuIsOpen ? (
        <MaterialSymbol icon="arrow_drop_up" color="var(--gray4b)" rounded />
      ) : (
        <MaterialSymbol icon="arrow_drop_down" color="var(--gray4b)" rounded />
      )}
    </components.DropdownIndicator>
  );
};

export type DropdownOption<TYPE> = {
  label: string;
  /** Different label when item is selected (optional) */
  labelSelected?: string;
  icon?: ReactNode;
  iconSelected?: boolean;
  value: TYPE;
  isDisabled?: boolean;
};

type IDropdown<TYPES extends string> = {
  initialText?: string;
  options: DropdownOption<TYPES>[];
  // optionDisabledText?: string;
  selected: undefined | TYPES;
  onChange: (select: TYPES) => void;
  slim?: boolean;
  required?: boolean;
  loading?: boolean;
  disabled?: boolean;
  hideSelected?: boolean;
  /** Note that on mobile it's automatically disabled */
  disabledSearchOnDesktop?: boolean;
  noBorder?: boolean;
  noPaddingRight?: boolean;
  alignTextRight?: boolean;

  /** Selected label color */
  singleValueColor?: string;
};

/** Every Dropdown must have unique ID */
let dropdownIdIndex = 1;

const getDropdownId = () => (dropdownIdIndex++).toString();

export default function Dropdown<TYPES extends string>({
  initialText,
  options,
  // optionDisabledText,
  selected,
  onChange,
  required,
  loading,
  disabled,
  slim,
  hideSelected = false,
  disabledSearchOnDesktop,
  noBorder,
  noPaddingRight,
  alignTextRight,
  singleValueColor,
}: IDropdown<TYPES>) {
  const isMobile = useIsMobile();
  const [id] = useState(getDropdownId());

  /** Disable searchable on mobile to prevent keyboard pop */
  const isSearchable = !isMobile && !disabledSearchOnDesktop;

  const selectedOption = options.find(option => option.value === selected);
  // If value is empty, we consider nothing being selected
  // const selectedOption = selectedOptionUnchecked?.value
  //   ? selectedOptionUnchecked
  //   : undefined;

  return (
    <Select
      // Name is required for HTML5 form validation (Required)
      name={`Dropdown-${id}`}
      required={required}
      inputId={id}
      // Uncomment to debug Dropdown opened
      // menuIsOpen
      aria-labelledby={id}
      value={selectedOption}
      onChange={(item: any) => {
        onChange(item.value);
      }}
      // Different label for list and for selected label,
      // eg. "GTC" is "Good until canceled" in opened list (BuySell panel)
      // https://stackoverflow.com/questions/61286833/react-select-different-labels-for-dropdown-list-and-for-input-field
      formatOptionLabel={
        ((option: DropdownOption<TYPES>, { context }: any) => {
          return context === 'menu' ? (
            <>
              {option.icon}
              {option.label}
            </>
          ) : (
            <>
              {option.iconSelected !== false ? option.icon : null}{' '}
              {option.labelSelected ?? option.label}
            </>
          );
        }) as any
      }
      // TODO: upgrade Dropdown
      menuPlacement="auto"
      isDisabled={disabled}
      isSearchable={isSearchable}
      placeholder={initialText}
      styles={selectStyles(
        noBorder,
        alignTextRight,
        slim,
        singleValueColor,
        noPaddingRight
      )}
      hideSelectedOptions={hideSelected}
      options={loading ? [] : options}
      menuShouldScrollIntoView
      theme={theme => ({
        ...theme,
        colors: {
          ...theme.colors,
          primary: 'var(--mainLight)',
        },
      })}
      components={{
        DropdownIndicator,
      }}
    />
  );
}
